import React, { useEffect, useId, useMemo, useRef } from 'react';
import { Box, Button, Flex, FlexItem, Text } from '@bigcommerce/big-design';
import {
  QUESTION_STATUSES,
  QUESTION_STATUS_LABELS,
  isValidAdminTransition,
  type ApiErrorResponse,
  type QuestionPatchSubOperation,
  type QuestionPatchSubOperationOutcome,
  type QuestionStatus,
  type QuestionStatusConflict,
} from '@find-my-part/types';

type QuestionPatchSubOpName = QuestionPatchSubOperation;
type QuestionPatchSubOpOutcome = QuestionPatchSubOperationOutcome;

export interface QuestionPatchSubOp {
  op: QuestionPatchSubOpName;
  outcome: QuestionPatchSubOpOutcome;
  /** Bounded, display-safe summary; arbitrary server values are never rendered. */
  observedSummary?: string;
}

export interface DecodedQuestionStatusConflict extends QuestionStatusConflict {
  subOps: QuestionPatchSubOp[];
}

export type QuestionStatusWriteOrigin = 'queue' | 'workspace' | 'detail';
export type QuestionStatusWriteAction = 'approve' | 'reject';
export type QuestionStatusWriteIssueKind =
  | 'conflict'
  | 'refresh_required'
  | 'stale_tab'
  | 'ambiguous'
  | 'partial';

/** Intended, uncommitted suffix fields retained by a future compound editor. */
export interface QuestionPatchSuffixDraft {
  tags?: string[];
  suggestedProductIds?: number[];
}

export interface QuestionPatchSuffixRecovery {
  draft: QuestionPatchSuffixDraft;
  onReapply: (draft: QuestionPatchSuffixDraft) => void;
}

interface QuestionStatusWriteIssueBase {
  questionId: string;
  action: QuestionStatusWriteAction;
  origin: QuestionStatusWriteOrigin;
  expectedStatus: QuestionStatus;
  targetStatus: QuestionStatus;
  /** Stable across refresh/recovery updates so one event is announced once. */
  eventId?: number;
  /** Static alert copy for the original event; mutable recovery stays outside it. */
  announcementKind?: QuestionStatusWriteIssueKind;
}

export type QuestionStatusWriteIssue =
  | (QuestionStatusWriteIssueBase & {
      kind: 'conflict';
      observedStatus: QuestionStatus;
      subOps: QuestionPatchSubOp[];
    })
  | (QuestionStatusWriteIssueBase & {
      kind: 'refresh_required';
      subOps: QuestionPatchSubOp[];
    })
  | (QuestionStatusWriteIssueBase & { kind: 'stale_tab' })
  | (QuestionStatusWriteIssueBase & {
      kind: 'ambiguous';
      subOps: QuestionPatchSubOp[];
    })
  | (QuestionStatusWriteIssueBase & {
      kind: 'partial';
      subOps: QuestionPatchSubOp[];
      /** Present after a recovery GET observes a later status change. */
      observedStatus?: QuestionStatus;
    });

const CONFLICT_KEYS = new Set(['surface', 'contested', 'expected', 'observed', 'recovery']);
const SUB_OP_KEYS = new Set(['op', 'outcome', 'observed']);
const SUB_OP_NAMES = new Set<QuestionPatchSubOpName>(['status', 'tags', 'products']);
const SUB_OP_OUTCOMES = new Set<QuestionPatchSubOpOutcome>([
  'committed',
  'conflicted',
  'not_attempted',
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function isQuestionStatus(value: unknown): value is QuestionStatus {
  return typeof value === 'string' && (QUESTION_STATUSES as readonly string[]).includes(value);
}

function readStatusState(value: unknown): { status: QuestionStatus } | null {
  if (!isRecord(value) || Object.keys(value).length !== 1 || !isQuestionStatus(value.status)) {
    return null;
  }
  return { status: value.status };
}

function observedSummary(op: QuestionPatchSubOpName, value: unknown): string | undefined {
  if (value === undefined) return undefined;
  if (op === 'status') {
    return isQuestionStatus(value) ? QUESTION_STATUS_LABELS[value] : undefined;
  }
  if (op === 'tags') {
    return Array.isArray(value) &&
      value.length <= 100 &&
      value.every((entry) => typeof entry === 'string')
      ? `${value.length} tag${value.length === 1 ? '' : 's'}`
      : undefined;
  }
  return Array.isArray(value) &&
    value.length <= 10 &&
    value.every((entry) => typeof entry === 'number' && Number.isInteger(entry) && entry > 0)
    ? `${value.length} product${value.length === 1 ? '' : 's'}`
    : undefined;
}

/** Decode the optional compound-PATCH report without ever trusting raw display values. */
export function readQuestionPatchSubOps(value: unknown): QuestionPatchSubOp[] {
  if (!Array.isArray(value) || value.length === 0 || value.length > SUB_OP_NAMES.size) return [];
  const seen = new Set<QuestionPatchSubOpName>();
  const decoded: QuestionPatchSubOp[] = [];
  for (const entry of value) {
    if (!isRecord(entry) || !Object.keys(entry).every((key) => SUB_OP_KEYS.has(key))) return [];
    if (
      typeof entry.op !== 'string' ||
      !SUB_OP_NAMES.has(entry.op as QuestionPatchSubOpName) ||
      typeof entry.outcome !== 'string' ||
      !SUB_OP_OUTCOMES.has(entry.outcome as QuestionPatchSubOpOutcome)
    ) {
      return [];
    }
    const op = entry.op as QuestionPatchSubOpName;
    if (seen.has(op)) return [];
    seen.add(op);
    decoded.push({
      op,
      outcome: entry.outcome as QuestionPatchSubOpOutcome,
      ...(observedSummary(op, entry.observed)
        ? { observedSummary: observedSummary(op, entry.observed) }
        : {}),
    });
  }
  return decoded;
}

/** Strictly decode ADR-027's bounded question-status conflict contract. */
export function readQuestionStatusConflict(
  error: ApiErrorResponse['error']
): DecodedQuestionStatusConflict | null {
  if (error.code !== 'STALE_WRITE') return null;
  const value = error.details?.conflict;
  if (!isRecord(value) || !Object.keys(value).every((key) => CONFLICT_KEYS.has(key))) return null;
  const expected = readStatusState(value.expected);
  const observed = readStatusState(value.observed);
  if (
    value.surface !== 'question_status' ||
    value.recovery !== 'direct' ||
    !expected ||
    !observed ||
    !Array.isArray(value.contested) ||
    value.contested.length !== 1 ||
    value.contested[0] !== 'status'
  ) {
    return null;
  }
  return {
    surface: 'question_status',
    contested: ['status'],
    expected,
    observed,
    recovery: 'direct',
    subOps: readQuestionPatchSubOps(error.details?.subOps),
  };
}

// -----------------------------------------------------------------------------
// ADR-027 slice 3 — tags/products conflict decoding (D-12 contract support).
// No production surface sends tags/suggestedProductIds on the question PATCH
// today, so this is deliberately decoder/contract-only: the future compound
// editor consumes it, and the display reduction is bounded counts/labels —
// never raw dumps of server arrays.
// -----------------------------------------------------------------------------

/** Stable marker for a non-conformant raw value; never echoed as a baseline. */
const MALFORMED_ARRAY_MARKER = 'malformed_raw_value';

/** Display-safe cap mirrored from the Worker-side observation projection. */
const ARRAY_CONFLICT_MAX_ENTRIES = 100;

export interface DecodedQuestionArrayConflict {
  surface: 'question_tags' | 'question_products';
  /** Validated baseline echo; null when the server marker-encoded it. */
  expected: string[] | number[] | null;
  /** Validated current server value; null when the server marker-encoded it. */
  observed: string[] | number[] | null;
  /** Bounded display label ("2 tags" / "3 products"); contents never rendered. */
  observedSummary?: string;
  recovery: 'direct' | 'refresh_required';
  subOps: QuestionPatchSubOp[];
}

function readConflictTagsValue(value: unknown): string[] | null | undefined {
  if (value === MALFORMED_ARRAY_MARKER) return null;
  if (
    Array.isArray(value) &&
    value.length <= ARRAY_CONFLICT_MAX_ENTRIES &&
    value.every((entry) => typeof entry === 'string')
  ) {
    return [...value];
  }
  return undefined;
}

function readConflictProductsValue(value: unknown): number[] | null | undefined {
  if (value === MALFORMED_ARRAY_MARKER) return null;
  if (
    Array.isArray(value) &&
    value.length <= ARRAY_CONFLICT_MAX_ENTRIES &&
    value.every((entry) => typeof entry === 'number' && Number.isFinite(entry))
  ) {
    return [...value];
  }
  return undefined;
}

/** Strictly decode ADR-027 slice 3's bounded tags/products conflict contract. */
export function readQuestionArrayConflict(
  error: ApiErrorResponse['error']
): DecodedQuestionArrayConflict | null {
  if (error.code !== 'STALE_WRITE') return null;
  const value = error.details?.conflict;
  if (!isRecord(value) || !Object.keys(value).every((key) => CONFLICT_KEYS.has(key))) return null;
  if (value.recovery !== 'direct' && value.recovery !== 'refresh_required') return null;
  const surface = value.surface;
  if (surface !== 'question_tags' && surface !== 'question_products') return null;
  const field = surface === 'question_tags' ? 'tags' : 'suggestedProductIds';
  if (
    !Array.isArray(value.contested) ||
    value.contested.length !== 1 ||
    value.contested[0] !== field
  ) {
    return null;
  }
  const readValue = surface === 'question_tags' ? readConflictTagsValue : readConflictProductsValue;
  const readState = (state: unknown): string[] | number[] | null | undefined => {
    if (!isRecord(state) || Object.keys(state).length !== 1 || !(field in state)) return undefined;
    return readValue(state[field]);
  };
  const expected = readState(value.expected);
  const observed = readState(value.observed);
  if (expected === undefined || observed === undefined) return null;
  // Marker discipline (D-4): a marker-encoded value never claims direct recovery.
  if ((expected === null || observed === null) && value.recovery !== 'refresh_required') {
    return null;
  }
  const opName: QuestionPatchSubOpName = surface === 'question_tags' ? 'tags' : 'products';
  const summary = observed === null ? undefined : observedSummary(opName, observed);
  return {
    surface,
    expected,
    observed,
    ...(summary ? { observedSummary: summary } : {}),
    recovery: value.recovery,
    subOps: readQuestionPatchSubOps(error.details?.subOps),
  };
}

/**
 * Convergence check for the D-12 "already at your target" copy: the observed
 * server value equals the operator's draft target under the same order-exact
 * canonical comparison the Worker predicate applies.
 */
export function isConvergedQuestionArrayConflict(
  conflict: DecodedQuestionArrayConflict | null,
  target: readonly (string | number)[] | undefined
): boolean {
  return (
    conflict !== null &&
    conflict.observed !== null &&
    target !== undefined &&
    JSON.stringify(conflict.observed) === JSON.stringify(target)
  );
}

/** D-13's exact new-UI/old-Worker strict-reader rejection. */
export function isQuestionExpectedStateStaleTabError(error: ApiErrorResponse['error']): boolean {
  return error.code === 'VALIDATION_ERROR' && error.details?.field === 'expectedState';
}

/** A mutation may have committed before these transport/server outcomes became visible. */
export function isAmbiguousQuestionStatusFailure(error: ApiErrorResponse['error']): boolean {
  return [
    'INTERNAL_ERROR',
    'NETWORK_ERROR',
    'REQUEST_TIMEOUT',
    'MAX_RETRIES_EXCEEDED',
    'SERVICE_UNAVAILABLE',
  ].includes(error.code);
}

export function isConvergedQuestionStatusIssue(issue: QuestionStatusWriteIssue | null): boolean {
  return issue?.kind === 'conflict' && issue.observedStatus === issue.targetStatus;
}

/** A compound request must never resend an already-committed status prefix. */
export function hasCommittedQuestionStatusPrefix(issue: QuestionStatusWriteIssue | null): boolean {
  return (
    issue !== null &&
    'subOps' in issue &&
    issue.subOps.some((subOp) => subOp.op === 'status' && subOp.outcome === 'committed')
  );
}

/** A direct recovery is offered only when the current-state transition is legal. */
export function canReapplyQuestionStatusIssue(issue: QuestionStatusWriteIssue | null): boolean {
  return (
    issue?.kind === 'conflict' &&
    !isConvergedQuestionStatusIssue(issue) &&
    !hasCommittedQuestionStatusPrefix(issue) &&
    isValidAdminTransition(issue.observedStatus, issue.targetStatus)
  );
}

function selectRetryableSuffix(
  issue: QuestionStatusWriteIssue | null,
  recovery: QuestionPatchSuffixRecovery | undefined
): QuestionPatchSuffixDraft | null {
  if (!recovery || !hasCommittedQuestionStatusPrefix(issue) || !issue || !('subOps' in issue)) {
    return null;
  }
  const suffix: QuestionPatchSuffixDraft = {};
  for (const subOp of issue.subOps) {
    if (subOp.outcome === 'committed') continue;
    if (subOp.op === 'tags' && recovery.draft.tags !== undefined) {
      suffix.tags = [...recovery.draft.tags];
    }
    if (subOp.op === 'products' && recovery.draft.suggestedProductIds !== undefined) {
      suffix.suggestedProductIds = [...recovery.draft.suggestedProductIds];
    }
  }
  return Object.keys(suffix).length > 0 ? suffix : null;
}

function hasUnfinishedSuffix(issue: QuestionStatusWriteIssue | null): boolean {
  return (
    issue !== null &&
    'subOps' in issue &&
    issue.subOps.some(
      (subOp) =>
        (subOp.op === 'tags' || subOp.op === 'products') && subOp.outcome !== 'committed'
    )
  );
}

interface QuestionStatusWriteNoticeProps {
  issue: QuestionStatusWriteIssue | null;
  busy: boolean;
  onReapply?: () => void;
  onRefresh?: () => void;
  onDismiss?: () => void;
  suffixRecovery?: QuestionPatchSuffixRecovery;
  restoreFocusTo?: () => HTMLElement | null;
  testId?: string;
}

const SR_ONLY_STYLE: React.CSSProperties = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
};

const SUB_OP_LABELS: Record<QuestionPatchSubOpName, string> = {
  status: 'Status',
  tags: 'Tags',
  products: 'Suggested products',
};

const SUB_OP_OUTCOME_LABELS: Record<QuestionPatchSubOpOutcome, string> = {
  committed: 'committed',
  conflicted: 'conflicted',
  not_attempted: 'not attempted',
};

function QuestionSubOpsSummary({ subOps }: { subOps: QuestionPatchSubOp[] }): React.ReactElement {
  return (
    <Box marginBottom="small">
      <Text bold marginBottom="xxSmall">
        What happened in this request:
      </Text>
      <ul aria-label="Question update outcomes" style={{ margin: 0 }}>
        {subOps.map((subOp) => (
          <li key={subOp.op}>
            {SUB_OP_LABELS[subOp.op]}: {SUB_OP_OUTCOME_LABELS[subOp.outcome]}
            {subOp.observedSummary ? ` (current: ${subOp.observedSummary})` : ''}
          </li>
        ))}
      </ul>
    </Box>
  );
}

/**
 * Persistent write-safety notice. The assertive region is mounted empty and
 * contains only a static event summary; mutable recovery content lives in the
 * separately named focusable region.
 */
export function QuestionStatusWriteNotice({
  issue,
  busy,
  onReapply,
  onRefresh,
  onDismiss,
  suffixRecovery,
  restoreFocusTo,
  testId = 'question-status-write',
}: QuestionStatusWriteNoticeProps): React.ReactElement {
  const regionRef = useRef<HTMLDivElement>(null);
  const previousAnnouncementIdentityRef = useRef<number | QuestionStatusWriteIssue | null>(null);
  const announcementSequenceRef = useRef(0);
  const previouslyVisibleRef = useRef(false);
  const focusRestorePendingRef = useRef(false);
  const restoreFocusToRef = useRef(restoreFocusTo);
  const headingId = useId();

  useEffect(() => {
    restoreFocusToRef.current = restoreFocusTo;
  }, [restoreFocusTo]);

  const announcementIdentity = issue?.eventId ?? issue;
  if (previousAnnouncementIdentityRef.current !== announcementIdentity) {
    previousAnnouncementIdentityRef.current = announcementIdentity;
    if (issue) announcementSequenceRef.current += 1;
  }

  useEffect(() => {
    const wasVisible = previouslyVisibleRef.current;
    previouslyVisibleRef.current = issue !== null;
    if (issue) {
      focusRestorePendingRef.current = false;
      regionRef.current?.focus();
      return undefined;
    }
    if (wasVisible) focusRestorePendingRef.current = true;
    if (busy || !focusRestorePendingRef.current || !restoreFocusToRef.current) return undefined;
    const timer = window.setTimeout(() => {
      const target = restoreFocusToRef.current?.();
      if (target && document.contains(target) && !target.hasAttribute('disabled')) {
        target.focus();
        focusRestorePendingRef.current = false;
      }
    }, 0);
    return () => window.clearTimeout(timer);
  }, [busy, issue]);

  const converged = isConvergedQuestionStatusIssue(issue);
  const statusPrefixCommitted = hasCommittedQuestionStatusPrefix(issue);
  const canReapplyStatus = canReapplyQuestionStatusIssue(issue);
  const retryableSuffixDraft = selectRetryableSuffix(issue, suffixRecovery);
  const unfinishedSuffix = hasUnfinishedSuffix(issue);
  const transitionNoLongerAvailable =
    issue?.kind === 'conflict' && !converged && !statusPrefixCommitted && !canReapplyStatus;
  const heading = useMemo(() => {
    if (!issue) return '';
    if (issue.kind === 'refresh_required') {
      return 'This question changed while you were editing.';
    }
    if (issue.kind === 'stale_tab') return 'The app was updated.';
    if (issue.kind === 'ambiguous') return 'Your change may have been saved.';
    if (issue.kind === 'partial') {
      return unfinishedSuffix ? 'Part of your update was saved.' : 'Your status change was saved.';
    }
    return 'Another staff member changed this since you loaded it.';
  }, [issue, unfinishedSuffix]);

  const announcementKind = issue?.announcementKind ?? issue?.kind;
  const announcement = issue
    ? announcementKind === 'conflict'
      ? 'Another staff member changed this question. Review the status conflict notice.'
      : announcementKind === 'refresh_required'
        ? 'This question changed while you were editing. Refresh to review the current status.'
        : announcementKind === 'stale_tab'
          ? 'The app was updated. Reload this page to continue.'
          : announcementKind === 'partial'
            ? unfinishedSuffix
              ? 'Part of your question update was saved. Review the update outcomes.'
              : 'Your question status was saved. Refresh to confirm the current status.'
            : 'Your change may have been saved. Refresh to confirm.'
    : '';

  return (
    <>
      <div role="alert" data-testid={`${testId}-alert`} style={SR_ONLY_STYLE}>
        {issue ? <span key={announcementSequenceRef.current}>{announcement}</span> : null}
      </div>
      {issue ? (
        <div
          ref={regionRef}
          role="region"
          aria-labelledby={headingId}
          tabIndex={-1}
          data-testid={`${testId}-notice`}
          style={{ outline: 'none' }}
        >
          <Box
            backgroundColor="warning10"
            borderRadius="normal"
            padding="small"
            marginBottom="medium"
          >
            <Text id={headingId} bold marginBottom="xxSmall">
              {heading}
            </Text>

            {issue.kind === 'conflict' ? (
              <>
                <Text marginBottom="xxSmall" data-testid={`${testId}-current`}>
                  Current status: {QUESTION_STATUS_LABELS[issue.observedStatus]}
                </Text>
                <Text marginBottom="small" data-testid={`${testId}-draft`}>
                  Your intended status: {QUESTION_STATUS_LABELS[issue.targetStatus]}
                </Text>
                {issue.subOps.length > 0 ? <QuestionSubOpsSummary subOps={issue.subOps} /> : null}
                <Text marginBottom="small">
                  {converged
                    ? `This question is already ${QUESTION_STATUS_LABELS[issue.targetStatus]} — no action needed.`
                    : statusPrefixCommitted
                      ? 'The status change was committed. This status control will not send it again; refresh before retrying any remaining fields.'
                      : transitionNoLongerAvailable
                        ? 'Your intended status is no longer available from the current status. Your input is preserved; use the current status and choose a new action if needed.'
                        : 'Review the current status, then refresh the baseline and reapply your unchanged choice.'}
                </Text>
              </>
            ) : issue.kind === 'refresh_required' ? (
              <>
                {issue.subOps.length > 0 ? <QuestionSubOpsSummary subOps={issue.subOps} /> : null}
                <Text marginBottom="small">
                  The conflict details could not be displayed safely. Your input is preserved;
                  refresh the current status before trying again.
                </Text>
              </>
            ) : issue.kind === 'stale_tab' ? (
              <Text marginBottom="small">
                The app was updated — reload this page to continue. Your input is preserved until
                you reload.
              </Text>
            ) : issue.kind === 'partial' ? (
              <>
                <Text marginBottom="xxSmall" data-testid={`${testId}-draft`}>
                  Committed status: {QUESTION_STATUS_LABELS[issue.targetStatus]}
                </Text>
                {issue.observedStatus ? (
                  <Text marginBottom="xxSmall" data-testid={`${testId}-current`}>
                    Current status: {QUESTION_STATUS_LABELS[issue.observedStatus]}
                  </Text>
                ) : null}
                <QuestionSubOpsSummary subOps={issue.subOps} />
                <Text marginBottom="small">
                  {issue.observedStatus && issue.observedStatus !== issue.targetStatus
                    ? unfinishedSuffix
                      ? 'The status operation committed, but the current status changed again afterward. This control will not send the committed operation again; reapply only unfinished edits from their editors.'
                      : 'The status operation committed, but the current status changed again afterward. This control will not send the committed operation again; use the current status and choose a new action if needed.'
                    : unfinishedSuffix
                      ? 'The status operation committed. This control will not send it again. Refresh the current status, then reapply only unfinished edits from their editors.'
                      : 'The status operation committed. This control will not send it again; refresh to confirm the current status.'}
                </Text>
              </>
            ) : (
              <>
                {issue.subOps.length > 0 ? <QuestionSubOpsSummary subOps={issue.subOps} /> : null}
                <Text marginBottom="small">
                  Your change may have been saved — refresh the question list to confirm before
                  trying again. Your input is preserved.
                </Text>
              </>
            )}

            <Flex flexGap="8px" flexWrap="wrap">
              {canReapplyStatus && onReapply ? (
                <FlexItem>
                  <Button
                    variant="primary"
                    onClick={onReapply}
                    disabled={busy}
                    isLoading={busy}
                    aria-label="Refresh and reapply my status change"
                  >
                    Refresh and reapply my change
                  </Button>
                </FlexItem>
              ) : null}
              {retryableSuffixDraft && suffixRecovery ? (
                <FlexItem>
                  <Button
                    variant="primary"
                    onClick={() => suffixRecovery.onReapply(retryableSuffixDraft)}
                    disabled={busy}
                    isLoading={busy}
                    aria-label="Reapply only my unfinished question edits"
                  >
                    Reapply unfinished edits
                  </Button>
                </FlexItem>
              ) : null}
              {(issue.kind === 'refresh_required' ||
                issue.kind === 'stale_tab' ||
                issue.kind === 'ambiguous' ||
                issue.kind === 'partial') &&
              onRefresh ? (
                <FlexItem>
                  <Button variant="primary" onClick={onRefresh} disabled={busy} isLoading={busy}>
                    {issue.kind === 'stale_tab'
                      ? 'Reload page'
                      : issue.kind === 'ambiguous'
                        ? 'Refresh to confirm'
                        : 'Refresh current status'}
                  </Button>
                </FlexItem>
              ) : null}
              {onDismiss ? (
                <FlexItem>
                  <Button variant="secondary" onClick={onDismiss} disabled={busy}>
                    {converged || statusPrefixCommitted || transitionNoLongerAvailable
                      ? 'Use current status'
                      : 'Dismiss'}
                  </Button>
                </FlexItem>
              ) : null}
            </Flex>
          </Box>
        </div>
      ) : null}
    </>
  );
}
